import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@platform/auth-client';

import { useForm } from 'react-hook-form';
import { QueryView } from '../QueryView.tsx';
import { CarrierConnections } from '../settings/CarrierConnections.tsx';
import { CARRIERS_MANAGE_ROLE } from '../settings/carriers.ts';
import { SenderFields } from '../settings/SenderFields.tsx';
import {
  senderBodyOf,
  senderFormOf,
  senderFormSchema,
  useSenderProfile,
  type SenderFormValues,
  type SenderProfile,
} from '../settings/sender.ts';
import { usePageContext } from '../usePageContext.ts';
import type { StepViewProps } from './step-flow.ts';
import styles from './Setup.module.css';

type SenderStepFormProps = StepViewProps & { profile: SenderProfile | null; canManage: boolean };

const SenderStepForm = ({ shipping, flow, profile, canManage }: SenderStepFormProps) => {
  const { t, invalidate } = usePageContext();
  const form = useForm<SenderFormValues>({
    resolver: zodResolver(senderFormSchema),
    defaultValues: senderFormOf(profile),
    mode: 'onBlur',
  });
  const saveSender = (values: SenderFormValues) => {
    flow.submit(async () => {
      await shipping.put('/admin/shipping/sender', { body: senderBodyOf(values) });
      await invalidate('sender');
    });
  };
  return (
    <form
      id={flow.formId}
      className={styles['form']}
      noValidate
      onSubmit={(event) => {
        if (canManage) {
          void form.handleSubmit(saveSender)(event);
          return;
        }
        event.preventDefault();
        flow.submit(() => Promise.resolve());
      }}
    >
      <fieldset className={styles['fieldset']} disabled={!canManage}>
        <legend className={styles['legend']}>{t('setup.senderTitle')}</legend>
        <SenderFields form={form} />
      </fieldset>
    </form>
  );
};

export const DeliveryStep = (props: StepViewProps) => {
  const { t } = usePageContext();
  const canManage = useAuth().hasRole(CARRIERS_MANAGE_ROLE);
  const sender = useSenderProfile(props.shipping);
  const senderForm = () => (
    <QueryView query={sender} label={t('setup.senderTitle')} height="280px">
      {(data) => <SenderStepForm {...props} profile={data.profile} canManage={canManage} />}
    </QueryView>
  );
  return (
    <div className={styles['stack']}>
      {canManage ? null : <p className={styles['notice']}>{t('setup.deliveryReadOnly')}</p>}
      <CarrierConnections api={props.api} shipping={props.shipping} canManage={canManage} />
      {senderForm()}
    </div>
  );
};
